/**
 * 가이드·홈 등에서 「행사 만들기」로 이동할 때
 * 총무/부총무 동호회만 대상으로 1개→직행, N개→선택 시트.
 *
 * getMyClubs 는 JOIN_APPROVE_DATE DESC 정렬이라 [0]이 「마지막 가입」이 된다.
 * 역할 필터 없이 [0]으로 보내면 일반회원 동호회로 가 행사 생성이 막힌다.
 */
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { MEMBER_ROLE } from '@/constants/codeConstants'
import type { Club } from '@/api/club'
import { useToast } from '@/composables/useToast'

/** EventCreateRounding01Page.checkAdminAccess 와 동일 */
const EVENT_CREATE_ROLES: string[] = [MEMBER_ROLE.MANAGER, MEMBER_ROLE.VICE_MANAGER]

export function filterEventCreateClubs(clubs: Club[]): Club[] {
  return clubs.filter((c) => EVENT_CREATE_ROLES.includes(c.memberRole || c.myMemberRole || ''))
}

export function useEventCreateNav() {
  const router = useRouter()
  const { error: showError } = useToast()

  const showClubPicker = ref(false)
  const pickerClubs = ref<Club[]>([])

  const goToEventCreate = (clubCode: string) => {
    showClubPicker.value = false
    router.push(`/mobile/club/${clubCode}/event/create/rounding`)
  }

  /**
   * @returns 'none' | 'one' | 'many'
   * none 일 때 호출측이 동호회 만들기 등으로 이어갈 수 있게 라우팅은 하지 않는다
   * (가이드는 toast만·홈은 draft 이어가기 등).
   */
  const navigateEventCreate = (clubs: Club[]): 'none' | 'one' | 'many' => {
    const managed = filterEventCreateClubs(clubs)
    if (managed.length === 0) {
      showError('행사 만들기는 총무·부총무 권한이 있는 동호회에서만 가능합니다. 동호회를 만들거나 권한을 확인해 주세요.')
      return 'none'
    }
    if (managed.length === 1) {
      const code = managed[0]?.clubCode
      if (!code) {
        showError('동호회 정보를 확인할 수 없습니다. 잠시 후 다시 시도해 주세요.')
        return 'none'
      }
      goToEventCreate(code)
      return 'one'
    }
    pickerClubs.value = managed
    showClubPicker.value = true
    return 'many'
  }

  const selectClubForEventCreate = (club: Club) => {
    if (!club.clubCode) return
    goToEventCreate(club.clubCode)
  }

  return {
    showClubPicker,
    pickerClubs,
    filterEventCreateClubs,
    navigateEventCreate,
    selectClubForEventCreate,
  }
}
